import React, { useState, useEffect, useRef, useMemo } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Box,
  Typography,
  IconButton,
  Tooltip,
  Avatar,
  CircularProgress,
  InputAdornment,
  Popover,
  ListItemText,
  Divider
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import VisibilityIcon from '@material-ui/icons/Visibility';
import GetAppIcon from '@material-ui/icons/GetApp';
import PictureAsPdfIcon from '@material-ui/icons/PictureAsPdf';
import DescriptionIcon from '@material-ui/icons/Description';
import InsertDriveFileIcon from '@material-ui/icons/InsertDriveFile';
import ImageIcon from '@material-ui/icons/Image';
import CheckIcon from '@material-ui/icons/Check';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import SearchIcon from '@material-ui/icons/Search';
import swal from 'sweetalert';

import TimelineInputField, {
  parseTimelineHoursMinutes,
  clampAndFormatTimeline,
  formatMinutesToTimeline
} from './TimelineInputField';
import WysiwygDescriptionEditor from './WysiwygDescriptionEditor';
import FilePreviewDialog, { downloadFileDirect } from './FilePreviewDialog';
import { resolveImageUrl } from '../../../utils/imageUrl';
import defaultAvatar from '../../../Images/person.jpg';
import AuthApi from '../../Services/Authapi';
import './TaskManagement.css';

// Tray Upload Icon matching main task specification
const TrayUploadIcon = ({ size = 20, style = {} }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.2"
    strokeLinecap="round"
    strokeLinejoin="round"
    style={style}
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" y1="3" x2="12" y2="15" />
  </svg>
);

const PRIORITY_OPTIONS = ['Low', 'Medium', 'High', 'Urgent'];

const parseTimelineMinutes = (val) => {
  const parsed = parseTimelineHoursMinutes(val);
  if (!parsed) return 0;
  return (parsed.h || 0) * 60 + (parsed.m || 0);
};

const formatFileSize = (bytes) => {
  if (!bytes || isNaN(bytes)) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
};

const SubTaskModal = ({
  open,
  onClose,
  onSave,
  mode = 'add', // 'add' | 'edit' | 'view'
  subTask = null,
  users = [],
  projectName = '',
  mainTaskTimeline = '',
  otherSubtasksMinutes = 0,
  mainTaskStatus = '',
  zIndex = 1300
}) => {
  const fileInputRef = useRef(null);

  const [name, setName] = useState('');
  const [memberIds, setMemberIds] = useState([]);
  const [priority, setPriority] = useState('Medium');
  const [status, setStatus] = useState('Pending');
  const [timeline, setTimeline] = useState('');
  const [files, setFiles] = useState([]);
  const [description, setDescription] = useState('');

  const [nameError, setNameError] = useState('');
  const [userError, setUserError] = useState('');
  const [uploadingFiles, setUploadingFiles] = useState(false);

  // User popover state (matches Add Task Select User)
  const [userPopoverAnchor, setUserPopoverAnchor] = useState(null);
  const [userSearchQuery, setUserSearchQuery] = useState('');
  const avatarsScrollRef = useRef(null);

  // Smooth horizontal scroll for selected user avatars
  useEffect(() => {
    const el = avatarsScrollRef.current;
    if (!el) return;
    const handleWheel = (e) => {
      if (e.deltaY !== 0) {
        e.preventDefault();
        el.scrollLeft += e.deltaY;
      }
    };
    el.addEventListener('wheel', handleWheel, { passive: false });
    return () => el.removeEventListener('wheel', handleWheel);
  }, [memberIds]);

  // File preview dialog state
  const [previewFile, setPreviewFile] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  const isView = mode === 'view';
  const isBacklogMain = String(mainTaskStatus || '').trim().toLowerCase() === 'backlog';
  const isDoneMain = String(mainTaskStatus || '').trim().toLowerCase() === 'done';

  const initializedIdRef = useRef(null);

  // Initialize state whenever open or subTask changes
  useEffect(() => {
    if (!open) {
      initializedIdRef.current = null;
      return;
    }

    const subTaskId = subTask ? (subTask.child_id || subTask.sub_task_id || subTask.id || 'current') : 'new';
    const initKey = `${mode}_${subTaskId}`;
    if (initializedIdRef.current === initKey) {
      return;
    }
    initializedIdRef.current = initKey;

    if (mode === 'edit' || mode === 'view') {
      setName(subTask?.name || subTask?.title || '');
      let initialMemberIds = [];
      if (Array.isArray(subTask?.member_ids)) {
        initialMemberIds = subTask.member_ids.map(Number);
      } else if (Array.isArray(subTask?.user_ids)) {
        initialMemberIds = subTask.user_ids.map(Number);
      } else if (typeof subTask?.member_ids === 'string' && subTask.member_ids.trim()) {
        try {
          const parsed = JSON.parse(subTask.member_ids);
          if (Array.isArray(parsed)) initialMemberIds = parsed.map(Number);
        } catch (e) {
          initialMemberIds = subTask.member_ids.split(',').map((s) => Number(s.trim())).filter(Boolean);
        }
      } else if (subTask?.developer_user_id || subTask?.user_id) {
        initialMemberIds = [Number(subTask.developer_user_id || subTask.user_id)];
      }
      setMemberIds(initialMemberIds);
      setPriority(subTask?.priority || 'Medium');
      setStatus(
        isDoneMain
          ? 'Done'
          : isBacklogMain
          ? 'Backlog'
          : subTask?.status || 'Pending'
      );
      setTimeline(subTask?.timeline || '');
      // Handle files (could be array or JSON string)
      let initialFiles = [];
      if (Array.isArray(subTask?.files)) {
        initialFiles = subTask.files;
      } else if (typeof subTask?.files === 'string' && subTask.files.trim()) {
        try {
          const parsed = JSON.parse(subTask.files);
          if (Array.isArray(parsed)) initialFiles = parsed;
        } catch (e) {
          initialFiles = [];
        }
      }
      setFiles(initialFiles);
      setDescription(subTask?.description || subTask?.details || '');
    } else {
      // Add mode defaults
      setName('');
      setMemberIds([]);
      setPriority('Medium');
      setStatus(isDoneMain ? 'Done' : isBacklogMain ? 'Backlog' : 'Pending');
      setTimeline('');
      setFiles([]);
      setDescription('');
    }
    setNameError('');
    setUserError('');
    setUserPopoverAnchor(null);
    setUserSearchQuery('');
  }, [open, mode, subTask, isBacklogMain, isDoneMain]);

  // Main task timeline minutes limit calculation
  const mainTaskMinutes = useMemo(() => {
    return parseTimelineMinutes(mainTaskTimeline);
  }, [mainTaskTimeline]);

  // Remaining minutes available for this subtask
  const remainingMinutes = useMemo(() => {
    if (!mainTaskMinutes || mainTaskMinutes <= 0) return null; // No upper cap if main task timeline not set
    const rem = mainTaskMinutes - (otherSubtasksMinutes || 0);
    return Math.max(0, rem);
  }, [mainTaskMinutes, otherSubtasksMinutes]);

  // Max minutes for this subtask is min(480, remainingMinutes)
  const effectiveMaxMinutes = useMemo(() => {
    const maxSubTaskMinutes = 8 * 60; // 480 mins
    if (remainingMinutes === null) return maxSubTaskMinutes;
    return Math.min(maxSubTaskMinutes, remainingMinutes);
  }, [remainingMinutes]);

  const handleMaxExceeded = () => {
    const maxSubTaskMinutes = 8 * 60;
    if (remainingMinutes === 0) {
      swal('Warning', 'Total subtask timeline has already reached the main task timeline limit.', 'warning');
    } else if (remainingMinutes !== null && remainingMinutes < maxSubTaskMinutes) {
      swal(
        'Warning',
        `Subtask timeline cannot exceed remaining main task timeline (${formatMinutesToTimeline(remainingMinutes)}).`,
        'warning'
      );
    } else {
      swal('Warning', 'Subtask timeline cannot exceed 08:00 hr.', 'warning');
    }
  };

  const handleFileSelect = async (e) => {
    const selected = Array.from(e.target.files || []);
    if (selected.length === 0) return;

    setUploadingFiles(true);
    try {
      const uploaded = await Promise.all(
        selected.map(async (f) => {
          try {
            const res = await AuthApi.uploadTaskFile(f);
            if (res && res.status && res.data) {
              return res.data;
            }
            if (res && res.file_url) {
              return res;
            }
            return {
              name: f.name,
              original_name: f.name,
              size: f.size,
              file_size: f.size,
              type: f.type
            };
          } catch (err) {
            console.error('File upload failed for', f.name, err);
            return {
              name: f.name,
              original_name: f.name,
              size: f.size,
              file_size: f.size,
              type: f.type
            };
          }
        })
      );
      const valid = uploaded.filter(Boolean);
      if (valid.length > 0) {
        setFiles((prev) => [...prev, ...valid]);
      }
    } catch (err) {
      console.error('Upload error:', err);
      swal('Error', 'An error occurred while uploading file(s).', 'error');
    } finally {
      setUploadingFiles(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemoveFile = (index) => {
    swal({
      title: 'Remove File',
      text: 'Are you sure you want to remove this attachment?',
      icon: 'warning',
      buttons: ['Cancel', 'Remove'],
      dangerMode: true
    }).then((confirmed) => {
      if (confirmed) {
        setFiles((prev) => prev.filter((_, i) => i !== index));
      }
    });
  };

  const handlePreviewFile = (file) => {
    setPreviewFile({
      name: file.name || file.original_name || 'Attachment',
      url: file.file_url || file.url || file.previewUrl || ''
    });
    setPreviewOpen(true);
  };

  const handleDownloadFile = (file) => {
    const url = file.file_url || file.url || '';
    const name = file.original_name || file.name || 'download';
    downloadFileDirect(url, name);
  };

  const handleSave = () => {
    let hasError = false;
    const trimmedName = (name || '').trim();
    if (!trimmedName) {
      setNameError('Subtask name is required.');
      hasError = true;
    } else {
      setNameError('');
    }

    if (!memberIds || memberIds.length === 0) {
      setUserError('Please select at least one user.');
      hasError = true;
    } else {
      setUserError('');
    }

    if (hasError) {
      return;
    }

    // Format timeline
    let finalTimeline = (timeline || '').trim();
    if (finalTimeline) {
      finalTimeline = clampAndFormatTimeline(finalTimeline, 8, effectiveMaxMinutes);
      if (finalTimeline && !finalTimeline.includes('hr')) {
        finalTimeline = `${finalTimeline} hr`;
      }
    }

    // Resolve assigned user info
    const primaryUserId = memberIds.length > 0 ? memberIds[0] : null;
    const selectedDevs = Array.isArray(users) ? users.filter((u) => memberIds.includes(Number(u.id))) : [];
    let selectedDevNames = selectedDevs
      .map((d) => (d.firstName ? `${d.firstName} ${d.lastName || ''}`.trim() : d.name))
      .join(', ');
    if (!selectedDevNames && subTask?.developer_name && memberIds.length > 0) {
      selectedDevNames = subTask.developer_name;
    }
    const selectedDevPhoto = selectedDevs.length > 0 && selectedDevs[0].photo
      ? resolveImageUrl(selectedDevs[0].photo)
      : (subTask?.user_photo || null);

    const resolvedStatus = isDoneMain
      ? 'Done'
      : isBacklogMain
      ? 'Backlog'
      : (status || 'Pending');

    const subTaskPayload = {
      ...(subTask || {}),
      name: trimmedName,
      title: trimmedName,
      member_ids: memberIds,
      user_ids: memberIds,
      user_id: primaryUserId,
      developer_user_id: primaryUserId,
      developer_name: selectedDevNames,
      user_name: selectedDevNames,
      user_photo: selectedDevPhoto,
      priority: priority || 'Medium',
      status: resolvedStatus,
      timeline: finalTimeline || '',
      files: files || [],
      description: description || '',
      details: description || trimmedName
    };

    if (typeof onSave === 'function') {
      onSave(subTaskPayload);
    }
  };

  const allUserIds = useMemo(() => {
    if (!Array.isArray(users)) return [];
    return users.map((u) => Number(u.id));
  }, [users]);

  const isAllSelected = useMemo(() => {
    if (allUserIds.length === 0) return false;
    return allUserIds.every((id) => memberIds.includes(id));
  }, [allUserIds, memberIds]);

  const handleToggleAll = () => {
    if (allUserIds.length === 0) return;
    const next = isAllSelected ? [] : [...allUserIds];
    setMemberIds(next);
    if (next.length > 0) {
      setUserError('');
    }
  };

  const handleToggleUser = (devId) => {
    const idNum = Number(devId);
    setMemberIds((prev) => {
      const exists = prev.includes(idNum);
      const next = exists ? prev.filter((id) => id !== idNum) : [...prev, idNum];
      if (next.length > 0) {
        setUserError('');
      }
      return next;
    });
  };

  const titleText = mode === 'add' ? 'Add Subtask' : (mode === 'edit' ? 'Edit Subtask' : 'Subtask Details');

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        scroll="paper"
        style={{ zIndex }}
        aria-labelledby="subtask-modal-title"
        disableScrollLock
        PaperProps={{
          style: {
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 8px 32px rgba(3, 109, 218, 0.18)',
            display: 'flex',
            flexDirection: 'column',
            maxHeight: 'calc(100vh - 48px)'
          }
        }}
      >
        {/* Header matching standard HRMS Dialog Header */}
        <DialogTitle
          id="subtask-modal-title"
          style={{
            position: 'relative',
            textAlign: 'center',
            padding: '12px 24px',
            background: 'linear-gradient(180deg, var(--hrms-primary-blue-start, #036dda) 0%, var(--hrms-primary-blue-end, #036dda) 100%)',
            color: '#ffffff',
            fontSize: '18px',
            fontWeight: 700,
            minHeight: '42px',
            lineHeight: 1.2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            margin: 0,
            flexShrink: 0
          }}
          disableTypography
        >
          {titleText}
          <CloseIcon
            style={{
              position: 'absolute',
              top: 10,
              right: 14,
              cursor: 'pointer',
              color: '#ffffff',
              fontSize: 22
            }}
            onClick={onClose}
          />
        </DialogTitle>

        {/* Modal Form Content - Using same classes & styling as Add Task (CreateTask.js) */}
        <DialogContent
          className="subtask-modal-content"
          style={{
            padding: '20px 24px',
            backgroundColor: '#ffffff',
            overflowY: 'auto',
            flex: '1 1 auto'
          }}
        >
          <form className="common-form-fields" onSubmit={(e) => { e.preventDefault(); handleSave(); }} noValidate>
            <Grid container spacing={2}>
              {/* 1. Subtask Name (label="Subtask Name" without literal * because required adds it automatically) */}
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  label="Subtask Name *"
                  placeholder="Enter subtask name..."
                  name="name"
                  value={name}
                  onChange={(e) => {
                    setName(e.target.value);
                    if (nameError) setNameError('');
                  }}
                  disabled={isView}
                  error={Boolean(nameError)}
                  helperText={nameError}
                />
              </Grid>

              {/* 2. Select User (Multiple Select - Attendance / CreateTask Popover Style) */}
              <Grid item xs={12} sm={6}>
                <div style={{ position: 'relative' }}>
                  <TextField
                    fullWidth
                    size="small"
                    variant="outlined"
                    label="Select User *"
                    placeholder={memberIds.length === 0 ? 'Select User' : undefined}
                    value=""
                    onClick={(e) => !isView && setUserPopoverAnchor((prev) => (prev ? null : e.currentTarget))}
                    disabled={isView}
                    error={Boolean(userError)}
                    helperText={userError}
                    InputLabelProps={{ shrink: true }}
                    InputProps={{
                      readOnly: true,
                      style: {
                        cursor: isView ? 'default' : 'pointer',
                        padding: '0 10px',
                        height: '40px',
                        minHeight: '40px',
                        display: 'flex',
                        alignItems: 'center'
                      },
                      startAdornment: memberIds.length > 0 ? (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            width: 'calc(100% - 24px)',
                            overflow: 'hidden'
                          }}
                        >
                          <span className="tm-selected-users-text" style={{ fontSize: 13 }}>
                            {memberIds.length} user(s) selected
                          </span>
                          <div
                            ref={avatarsScrollRef}
                            className="tm-avatars-scroll"
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px',
                              overflowX: 'auto',
                              overflowY: 'hidden',
                              maxWidth: '100%',
                              padding: '2px 16px 2px 4px',
                              flex: 1,
                              marginRight: 6
                            }}
                          >
                            {memberIds.map((uid) => {
                              const u = Array.isArray(users)
                                ? users.find((d) => Number(d.id) === Number(uid))
                                : null;
                              const uName = u
                                ? (u.firstName ? `${u.firstName} ${u.lastName || ''}`.trim() : u.name)
                                : `User #${uid}`;
                              return (
                                <Tooltip key={uid} title={uName} arrow>
                                  <Avatar
                                    src={u && u.photo && String(u.photo).trim() !== '' ? resolveImageUrl(u.photo) : defaultAvatar}
                                    imgProps={{
                                      onError: (e) => {
                                        if (e.target.src !== defaultAvatar) {
                                          e.target.src = defaultAvatar;
                                        }
                                      }
                                    }}
                                    style={{
                                      width: 24,
                                      height: 24,
                                      flexShrink: 0,
                                      backgroundColor: 'transparent',
                                      border: '1.5px solid #ffffff',
                                      boxShadow: '0 1px 3px rgba(0,0,0,0.18)'
                                    }}
                                  />
                                </Tooltip>
                              );
                            })}
                            <div style={{ minWidth: 16, width: 16, flexShrink: 0, height: 1 }} />
                          </div>
                        </div>
                      ) : (
                        <span className="tm-select-user-placeholder" style={{ fontSize: 13 }}>
                          Select User
                        </span>
                      ),
                      endAdornment: (
                        <InputAdornment position="end" style={{ marginLeft: 'auto' }}>
                          <ExpandMoreIcon style={{ cursor: isView ? 'default' : 'pointer', color: '#666', fontSize: 20 }} />
                        </InputAdornment>
                      ),
                      inputProps: {
                        readOnly: true,
                        style: { display: 'none' }
                      }
                    }}
                  />

                  <Popover
                    open={Boolean(userPopoverAnchor)}
                    anchorEl={userPopoverAnchor}
                    onClose={() => {
                      setUserPopoverAnchor(null);
                      setUserSearchQuery('');
                    }}
                    disableScrollLock
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
                    style={{ zIndex: Math.max(3000, (zIndex || 1300) + 200) }}
                    PaperProps={{
                      className: 'attendance-user-popover-paper',
                      onClick: (e) => e.stopPropagation(),
                      style: {
                        width: 300,
                        marginTop: 5,
                        borderRadius: 8,
                        boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
                        backgroundColor: '#ffffff',
                        overflow: 'hidden'
                      }
                    }}
                  >
                    <Box p={1.5} className="attendance-user-popover-header" style={{ backgroundColor: '#036dda', padding: '12px 14px' }}>
                      <Typography
                        variant="subtitle2"
                        className="attendance-user-popover-title"
                        style={{
                          fontWeight: 700,
                          marginBottom: 8,
                          color: '#ffffff'
                        }}
                      >
                        Select User
                      </Typography>
                      <TextField
                        className="common-user-search-field"
                        fullWidth
                        size="small"
                        placeholder="Search users..."
                        variant="outlined"
                        value={userSearchQuery}
                        onChange={(e) => setUserSearchQuery(e.target.value)}
                        autoFocus
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <SearchIcon fontSize="small" style={{ color: '#036dda' }} />
                            </InputAdornment>
                          )
                        }}
                      />
                    </Box>
                    <Divider />
                    <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                      {Array.isArray(users) && users.length > 0 ? (
                        <>
                          <MenuItem
                            className={`attendance-user-option ${isAllSelected ? 'attendance-user-selected' : ''}`}
                            disabled={!users || users.length === 0}
                            selected={isAllSelected}
                            onClick={handleToggleAll}
                            style={{
                              padding: '6px 14px',
                              backgroundColor: isAllSelected ? '#036dda' : undefined,
                              color: isAllSelected ? '#ffffff' : undefined
                            }}
                          >
                            <ListItemText
                              primary="All Users"
                              primaryTypographyProps={{
                                style: {
                                  color: isAllSelected ? '#ffffff' : 'inherit',
                                  fontWeight: isAllSelected ? 600 : 400,
                                  fontSize: 13
                                }
                              }}
                            />
                            {isAllSelected && (
                              <CheckIcon style={{ color: '#ffffff', fontSize: '1.1rem', marginLeft: 'auto' }} />
                            )}
                          </MenuItem>
                          {users
                            .filter((dev) => {
                              const q = (userSearchQuery || '').toLowerCase();
                              const uName = (dev.firstName ? `${dev.firstName} ${dev.lastName || ''}` : dev.name || '').toLowerCase();
                              return uName.includes(q);
                            })
                            .map((dev) => {
                              const devDisplayName = dev.firstName
                                ? `${dev.firstName} ${dev.lastName || ''}`.trim()
                                : dev.name;
                              const isChecked = memberIds.includes(Number(dev.id));
                              return (
                                <MenuItem
                                  key={dev.id}
                                  className={`attendance-user-option ${isChecked ? 'attendance-user-selected' : ''}`}
                                  selected={isChecked}
                                  onClick={() => handleToggleUser(dev.id)}
                                  style={{
                                    padding: '6px 14px',
                                    backgroundColor: isChecked ? '#036dda' : undefined,
                                    color: isChecked ? '#ffffff' : undefined
                                  }}
                                >
                                  <Box display="flex" alignItems="center" width="100%">
                                    <Avatar
                                      src={dev && dev.photo && String(dev.photo).trim() !== '' ? resolveImageUrl(dev.photo) : defaultAvatar}
                                      imgProps={{
                                        onError: (e) => {
                                          if (e.target.src !== defaultAvatar) {
                                            e.target.src = defaultAvatar;
                                          }
                                        }
                                      }}
                                      style={{
                                        width: 28,
                                        height: 28,
                                        marginRight: 10,
                                        border: isChecked ? '2px solid #ffffff' : 'none',
                                        backgroundColor: 'transparent'
                                      }}
                                    />
                                    <ListItemText
                                      primary={devDisplayName}
                                      primaryTypographyProps={{
                                        style: {
                                          color: isChecked ? '#ffffff' : 'inherit',
                                          fontWeight: isChecked ? 600 : 400,
                                          fontSize: 13
                                        }
                                      }}
                                    />
                                    {isChecked && (
                                      <CheckIcon style={{ color: '#ffffff', fontSize: '1.1rem', marginLeft: 'auto' }} />
                                    )}
                                  </Box>
                                </MenuItem>
                              );
                            })}
                        </>
                      ) : (
                        <div style={{ padding: '20px 16px', textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>
                          {!projectName ? 'Please select a project first' : 'No users assigned to this project'}
                        </div>
                      )}
                    </div>
                  </Popover>
                </div>
              </Grid>

              {/* 3. Priority */}
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth variant="outlined" size="small" disabled={isView}>
                  <InputLabel id="subtask-priority-label">Priority</InputLabel>
                  <Select
                    labelId="subtask-priority-label"
                    label="Priority"
                    value={priority || 'Medium'}
                    onChange={(e) => setPriority(e.target.value)}
                    MenuProps={{
                      disableScrollLock: true,
                      className: 'subtask-menu-popover',
                      getContentAnchorEl: null,
                      anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                      transformOrigin: { vertical: 'top', horizontal: 'left' },
                      style: { zIndex: Math.max(3000, (zIndex || 1300) + 200) },
                      PopoverProps: {
                        className: 'subtask-menu-popover',
                        style: { zIndex: Math.max(3000, (zIndex || 1300) + 200) }
                      }
                    }}
                    style={{ height: 40 }}
                  >
                    {PRIORITY_OPTIONS.map((p) => (
                      <MenuItem key={p} value={p}>
                        {p}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              {/* 4. Status */}
              <Grid item xs={12} sm={6}>
                <Tooltip
                  title={
                    isBacklogMain
                      ? 'Main task is in Backlog, so subtask status is locked to Backlog'
                      : isDoneMain
                      ? 'Main task is in Done, so subtask status is locked to Done'
                      : ''
                  }
                  arrow
                  placement="top"
                >
                  <FormControl
                    fullWidth
                    variant="outlined"
                    size="small"
                    disabled={isView || isBacklogMain || isDoneMain}
                  >
                    <InputLabel id="subtask-status-label">
                      {isBacklogMain || isDoneMain ? 'Status (Locked)' : 'Status'}
                    </InputLabel>
                    <Select
                      labelId="subtask-status-label"
                      label={isBacklogMain || isDoneMain ? 'Status (Locked)' : 'Status'}
                      value={isBacklogMain ? 'Backlog' : (isDoneMain ? 'Done' : (status || 'Pending'))}
                      onChange={(e) => {
                        if (!isBacklogMain && !isDoneMain) {
                          setStatus(e.target.value);
                        }
                      }}
                      MenuProps={{
                        disableScrollLock: true,
                        className: 'subtask-menu-popover',
                        getContentAnchorEl: null,
                        anchorOrigin: { vertical: 'bottom', horizontal: 'left' },
                        transformOrigin: { vertical: 'top', horizontal: 'left' },
                        style: { zIndex: Math.max(3000, (zIndex || 1300) + 200) },
                        PopoverProps: {
                          className: 'subtask-menu-popover',
                          style: { zIndex: Math.max(3000, (zIndex || 1300) + 200) }
                        }
                      }}
                      style={{ height: 40 }}
                    >
                      <MenuItem value="Backlog">Backlog</MenuItem>
                      <MenuItem value="Pending">To Do / Pending</MenuItem>
                      <MenuItem value="In Progress">In Progress</MenuItem>
                      <MenuItem value="Testing">Testing / Review</MenuItem>
                      <MenuItem value="Done">Done</MenuItem>
                    </Select>
                  </FormControl>
                </Tooltip>
              </Grid>

              {/* 5. Timeline */}
              <Grid item xs={12} sm={6}>
                <TimelineInputField
                  label="Add time line"
                  placeholder="00:00"
                  name="timeline"
                  id="subtask_timeline"
                  size="small"
                  value={timeline}
                  onChange={(val) => setTimeline(val)}
                  disabled={isView}
                  maxMinutes={effectiveMaxMinutes}
                  maxHours={Math.min(8, Math.floor(effectiveMaxMinutes / 60))}
                  helperText={
                    effectiveMaxMinutes < 480
                      ? `Max ${formatMinutesToTimeline(effectiveMaxMinutes)} (Remaining)`
                      : 'Max 8 hours'
                  }
                  onMaxExceeded={handleMaxExceeded}
                />
              </Grid>

              {/* 6. Upload file and photos (Exact same as CreateTask.js lines 1216-1316) */}
              <Grid item xs={12} sm={6}>
                <input
                  type="file"
                  ref={fileInputRef}
                  id="subtask-files-input"
                  multiple
                  accept="image/*,.jpg,.jpeg,.png,.webp,.gif,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                  style={{ display: 'none' }}
                  onChange={handleFileSelect}
                />
                <TextField
                  fullWidth
                  size="small"
                  variant="outlined"
                  placeholder="Upload Files & Photos"
                  value={files.length > 0 ? `${files.length} file${files.length > 1 ? 's' : ''} selected` : ''}
                  onClick={() => !isView && fileInputRef.current && fileInputRef.current.click()}
                  inputProps={{
                    readOnly: true,
                    style: {
                      cursor: isView ? 'default' : 'pointer',
                      textOverflow: 'ellipsis',
                      fontSize: 13,
                      padding: '10px 14px'
                    }
                  }}
                  InputProps={{
                    style: {
                      cursor: isView ? 'default' : 'pointer',
                      height: '40px',
                      minHeight: '40px'
                    },
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Upload file (Images, Docs, PDF, Word)" arrow placement="top">
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation();
                              !isView && fileInputRef.current && fileInputRef.current.click();
                            }}
                            className="tm-desc-upload-btn"
                            aria-label="Upload file"
                            disabled={isView || uploadingFiles}
                            style={{ padding: 4 }}
                          >
                            {uploadingFiles ? (
                              <CircularProgress size={18} style={{ color: '#036dda' }} />
                            ) : (
                              <TrayUploadIcon size={18} />
                            )}
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    )
                  }}
                />

                {/* Selected Files List with .tm-selected-file-item */}
                {files.length > 0 && (
                  <div style={{ marginTop: 8, display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
                    {files.map((file, idx) => {
                      const fileName = file.original_name || file.name || `Attachment ${idx + 1}`;
                      const fileSize = file.file_size ? formatFileSize(file.file_size) : (file.size ? formatFileSize(file.size) : '');
                      const isImg = (file.type && file.type.startsWith('image/')) || (file.mime_type && file.mime_type.startsWith('image/')) || /\.(jpg|jpeg|png|gif|webp|svg)$/i.test(fileName);
                      const isPdf = file.type === 'application/pdf' || (file.mime_type === 'application/pdf') || fileName.toLowerCase().endsWith('.pdf');
                      const isWord = fileName.toLowerCase().endsWith('.doc') || fileName.toLowerCase().endsWith('.docx');
                      return (
                        <div key={idx} className="tm-selected-file-item">
                          <div style={{ display: 'flex', alignItems: 'center', gap: 6, overflow: 'hidden', minWidth: 0, flex: 1 }}>
                            {isImg ? (
                              <ImageIcon style={{ color: '#0284c7', fontSize: 18, flexShrink: 0 }} />
                            ) : isPdf ? (
                              <PictureAsPdfIcon style={{ color: '#ef4444', fontSize: 18, flexShrink: 0 }} />
                            ) : isWord ? (
                              <DescriptionIcon style={{ color: '#2563eb', fontSize: 18, flexShrink: 0 }} />
                            ) : (
                              <InsertDriveFileIcon style={{ color: '#64748b', fontSize: 18, flexShrink: 0 }} />
                            )}
                            <span
                              style={{
                                fontWeight: 500,
                                color: 'var(--tm-text)',
                                textOverflow: 'ellipsis',
                                overflow: 'hidden',
                                whiteSpace: 'nowrap',
                                fontSize: 12,
                                cursor: 'pointer'
                              }}
                              onClick={() => handlePreviewFile(file)}
                              title={fileName}
                            >
                              {fileName}
                            </span>
                            {fileSize && (
                              <span style={{ fontSize: 10, color: '#64748b', flexShrink: 0 }}>
                                ({fileSize})
                              </span>
                            )}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
                            <IconButton
                              size="small"
                              onClick={() => handlePreviewFile(file)}
                              style={{ color: '#036dda', padding: 2 }}
                              title="Preview file"
                            >
                              <VisibilityIcon style={{ fontSize: 16 }} />
                            </IconButton>
                            <IconButton
                              size="small"
                              onClick={() => handleDownloadFile(file)}
                              style={{ color: '#059669', padding: 2 }}
                              title="Download file"
                            >
                              <GetAppIcon style={{ fontSize: 16 }} />
                            </IconButton>
                            {!isView && (
                              <IconButton
                                size="small"
                                onClick={() => handleRemoveFile(idx)}
                                style={{ color: '#94a3b8', padding: 2, marginLeft: 4 }}
                                title="Remove file"
                              >
                                <CloseIcon style={{ fontSize: 16 }} />
                              </IconButton>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Grid>

              {/* 7. Description */}
              <Grid item xs={12}>
                <WysiwygDescriptionEditor
                  label="Description"
                  placeholder="Enter subtask description..."
                  name="description"
                  id="subtask_description"
                  value={description || ''}
                  onChange={(html) => setDescription(html)}
                  onFileUpload={(uploaded) => {
                    if (uploaded) {
                      setFiles((prev) => [...prev, uploaded]);
                    }
                  }}
                  rows={4}
                  readOnly={isView}
                />
              </Grid>
            </Grid>
          </form>
        </DialogContent>

        {/* Modal Actions - HRMS standard buttons */}
          <DialogActions
          style={{
            padding: '14px 24px',
            borderTop: '1px solid #e2e8f0',
            backgroundColor: '#f8fafc',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 12,
            flexShrink: 0
          }}
        >
          {isView ? (
            <Button
              type="button"
              className="btn-cancel"
              style={{ textTransform: 'none' }}
              onClick={onClose}
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                type="button"
                className="btn-cancel"
                style={{ textTransform: 'none' }}
                onClick={onClose}
              >
                Cancel
              </Button>
              <Button
                type="button"
                className="btn-success"
                style={{
                  textTransform: 'none',
                  backgroundColor: '#036dda',
                  color: '#ffffff'
                }}
                variant="contained"
                onClick={handleSave}
                disabled={uploadingFiles}
              >
                {mode === 'edit' ? 'Save' : 'Save'}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* File Preview Modal */}
      {previewOpen && previewFile && (
        <FilePreviewDialog
          open={previewOpen}
          file={previewFile}
          zIndex={Math.max(3200, (zIndex || 1300) + 400)}
          onClose={() => {
            setPreviewOpen(false);
            setPreviewFile(null);
          }}
        />
      )}
    </>
  );
};

export default SubTaskModal;
